import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  calculateAndSaveExpat,
  getNonSelectedPlanCover,
  getProposalExpatDetails,
  issuePolicyExpat,
  saveProposalDraftExpat,
  downloadExpatDocument,
} from './queries';
import { validateProposalPageForm } from './validation';
import {
  convertProposalPageStateToSubmission,
  IProposalDetailsExpatPageSubmissionModel,
} from './submission';
import { toast } from 'react-toastify';
import { isEmpty, isValidNumber } from '@utils/validationUtils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import EnhancedStepper from '@components/stepper/EnhancedStepper';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import { useParams } from 'react-router-dom';
import CloseProposalDrawer from '../../close-proposal-drawer/CloseProposalDrawer';
import StaticLayout from '@components/layout/StaticLayout';
import Loader from '@components/loader/Loader';
import { ComissionProposalWidget } from '../../proposal-details-page/custom-widgets/commission/ComissionProposalWidget';
import { getProposalStatus, LookupToList } from './utils';
import PolicyProposalQuoteWidget from '../../proposal-details-page/custom-widgets/PolicyProposalQuoteWidget';
import PolicyCoversRepeaterWidget from '../../proposal-details-page/custom-widgets/PolicyCoversRepeaterWidget';
import PolicyCostChargesWidget from '../../proposal-details-page/custom-widgets/PolicyCostChargesWidget';
import PolicyInsuredWidget from '../../proposal-details-page/custom-widgets/PolicyInsuredWidget';
import {
  getProposalPageFormState,
  IProposalCover,
  IProposalPageFormState,
} from './form';
import {
  convertToDetailsSummary,
  convertToPolicyPersons,
  convertToPolicyPlanProposalCovers,
  convertToProposalCover,
  getPlanCoversLov,
  IProposalDetailsPageResponse,
  IProposalDetailsSummary,
} from './index2';
import PolicyDetailsEntityInfo from '../../proposal-details-page/custom-widgets/PolicyDetailsEntityInfo';
import { PolicyDetailsWidget } from '../../proposal-details-page/custom-widgets/PolicyDetailsWidget';
import PolicyPayerWidget from '../../proposal-details-page/custom-widgets/PolicyPayerWidget';
import PolicyOwnerWidget from '../../proposal-details-page/custom-widgets/PolicyOwnerWidget';
import PolicyBeneficiaryWidget from '../../proposal-details-page/custom-widgets/PolicyBeneficiaryWidget';
import ProposalExpatDetailsWidget from '../../proposal-details-page/custom-widgets/ProposalExpatDetailsWidget';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { getError } from '@utils/graph-utils';
import { downloadFileFromUrl, generateDownloadLink } from '@utils/file-utils';
import { IConfirmation } from '@redux/confirmation/types';
import { OpenConfirmationAction } from '@redux/confirmation/actions';
import { useAppDispatch } from '@redux/hooks';
import { IEnhancedMenuItem } from '@components/table/types';
import SimpleActionBar from '@components/menu/SimpleActionBar';
import { useStepperStyles, useTabsLayoutStyles } from '@styles/sharedStyles';

const ProposalExpatDetailsPage: React.FC = () => {
  const { classes: tabClasses } = useTabsLayoutStyles();
  const { classes: stepperClasses } = useStepperStyles({
    mainContainerRatio: 8,
  });

  const [closeProposalDrawerOpen, setCloseProposalDrawerOpen] =
    useState<boolean>(false);

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const params = useParams();
  const entityId = params.id;

  const [pageState, onPageStateUpdate] = useState<IProposalPageFormState>(
    getProposalPageFormState()
  );

  const [data, setData] = useState<IProposalDetailsSummary>();

  const ownerIsDifferent: boolean =
    pageState?.values?.policyPersons?.payer?.ownerIsDifferent || false;

  const [coversList, setCoversList] = useState<IProposalCover[]>([]);

  const [planCoversList, setPlanCoversList] = useState<
    Record<string, IProposalCover>
  >({});

  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({
    planCovers: {},
  });

  const [saveAndCalculationAction] = useMutation(calculateAndSaveExpat());

  const [saveAsDraftAction] = useMutation(saveProposalDraftExpat());

  const [issuePolicyExpatAction] = useMutation(issuePolicyExpat());

  const [downloadAction] = useMutation(downloadExpatDocument());

  const dispatch = useAppDispatch();

  useEffect(() => {
    initialize();
  }, []);

  const isClosed =
    data?.Status === 'CLOSED_WON' || data?.Status === 'CLOSED_LOST';

  const disabledPage = isClosed || submitting || loading;

  const [getProposalDetailsLazy] = useLazyQuery(getProposalExpatDetails());

  const [getNonSelectedPlanCoverLazy] = useLazyQuery(getNonSelectedPlanCover());

  const getPlanCoverDetails = async (
    planId: string,
    planCoverIds: string[]
  ) => {
    const { data } = await getNonSelectedPlanCoverLazy({
      variables: {
        selectedPlanID: planId,
        selectedPlanCoverIDs: planCoverIds,
      },
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
    });

    return data;
  };

  const initialize = async () => {
    setLoading(true);
    const result: { data: IProposalDetailsPageResponse } =
      await getProposalDetailsLazy({
        variables: { id: entityId },
        errorPolicy: 'all',
        fetchPolicy: 'no-cache',
      });

    const summaryData = convertToDetailsSummary(result.data);

    const initialCovers = convertToPolicyPlanProposalCovers(result.data);
    const initialPersons = convertToPolicyPersons(result.data);

    const planCoverDetails = await getPlanCoverDetails(
      summaryData.PlanID.Id,
      []
    );

    const lists = LookupToList(result.data);
    setLovs({
      medicalClasses: lists.medicalClasses,
      emergencyOptions: lists.emergencyOptions,
      continuityOptions: lists.continuityOptions,
      planCovers: getPlanCoversLov(planCoverDetails),
    });

    setPlanCoversList(
      convertToProposalCover(
        planCoverDetails,
        summaryData.PolicyCurrency?.Code,
        summaryData.PricingOption.PlanToPolicyRate,
        summaryData.PolicyEffectiveDate,
        summaryData.PolicyExpiryDate
      )
    );
    setCoversList(initialCovers);
    onPageStateUpdate(
      getProposalPageFormState(summaryData, initialCovers, initialPersons)
    );
    setData(summaryData);
    setLoading(false);
  };

  const renderLeftSection = () => {
    if (data) {
      return <PolicyDetailsEntityInfo data={data as any} />;
    }

    return <></>;
  };

  const onSubmit = async () => {
    const validationResult = validateProposalPageForm(data, pageState.values);

    if (!submitting) {
      setSubmitting(true);
      const newPageState = {
        ...pageState,
        errors: validationResult.errors,
        touched: validationResult.touched,
      };

      onPageStateUpdate(newPageState);
      if (validationResult.isValid) {
        const dataToSubmit = convertProposalPageStateToSubmission(
          data,
          pageState
        );
        const result = await saveAndCalculationAction({
          variables: { ...dataToSubmit },
          errorPolicy: 'all',
        });
        if (isEmpty(result.errors)) {
          // setDeactivateButtonState("success");
          toast.success(
            <ToastSuccessMessage>
              {'Proposal successfully saved.'}
            </ToastSuccessMessage>
          );
          initialize();
        } else {
          // setRegenerateButtonState(undefined);
          toast.error(
            <ToastErrorMessage>{getError(result)}</ToastErrorMessage>
          );
        }
      } else {
        toast.error(<ToastErrorMessage>Incomplete Form</ToastErrorMessage>);
      }
      setSubmitting(false);
    }
  };

  const onSaveDraft = async () => {
    if (!submitting) {
      setSubmitting(true);
      const dataToSubmit = convertProposalPageStateToSubmission(
        data,
        pageState
      ) as Partial<IProposalDetailsExpatPageSubmissionModel>;

      if (isEmpty(dataToSubmit.policyPersonInput[0]?.personId)) {
        dataToSubmit.policyPersonInput = [];
      }

      const result = await saveAsDraftAction({
        variables: { ...dataToSubmit },
        errorPolicy: 'all',
      });

      if (isEmpty(result.errors)) {
        // setDeactivateButtonState("success");
        toast.success(
          <ToastSuccessMessage>
            {'Proposal successfully saved.'}
          </ToastSuccessMessage>
        );

        initialize();
      } else {
        // setRegenerateButtonState(undefined);
        toast.error(<ToastErrorMessage>{getError(result)}</ToastErrorMessage>);
      }
    }
    setSubmitting(false);
  };

  const onIssuePolicyAction = async () => {
    if (!submitting) {
      setSubmitting(true);
      const dataToSubmit = {
        entityId: data.Id,
        clauseText: data.ClauseText || '',
      };
      const result = await issuePolicyExpatAction({
        variables: { ...dataToSubmit },
        errorPolicy: 'all',
      });

      if (isEmpty(result.errors)) {
        // setDeactivateButtonState("success");
        toast.success(
          <ToastSuccessMessage>
            {'Policy successfully issued.'}
          </ToastSuccessMessage>
        );
        const filesInfo = getFileDetailsList(result.data);
        for (const fileInfo of filesInfo) {
          const downloadLink = generateDownloadLink(
            fileInfo.fileId,
            fileInfo.EntityTypeId,
            fileInfo.EntityId,
            fileInfo.EntityViewId,
            fileInfo.PropertyId
          );
          await downloadFileFromUrl(downloadLink, fileInfo.fileName);
          setSubmitting(false);
        }
        initialize();
      } else {
        // setRegenerateButtonState(undefined);
        setSubmitting(false);
        toast.error(<ToastErrorMessage>{getError(result)}</ToastErrorMessage>);
      }
    }
  };

  const downloadDocument = async () => {
    if (!submitting) {
      setSubmitting(true);
      const dataToSubmit = {
        entityId: data.Id,
      };
      const result = await downloadAction({
        variables: { ...dataToSubmit },
        errorPolicy: 'all',
      });
      if (isEmpty(result.errors)) {
        // setDeactivateButtonState("success");
        const fileInfo = getFileDetails(result.data);
        const downloadLink = generateDownloadLink(
          fileInfo.fileId,
          fileInfo.EntityTypeId,
          fileInfo.EntityId,
          fileInfo.EntityViewId,
          fileInfo.PropertyId
        );

        await downloadFileFromUrl(downloadLink, fileInfo.fileName);
        toast.success(
          <ToastSuccessMessage>
            {'Policy sample successfully downloaded.'}
          </ToastSuccessMessage>
        );
        setSubmitting(false);
        // onSuccess();
      } else {
        // setRegenerateButtonState(undefined);
        setSubmitting(false);
        toast.error(<ToastErrorMessage>{getError(result)}</ToastErrorMessage>);
      }
    }
  };

  const renderActions = () => {
    const actions: IEnhancedMenuItem[] = [
      {
        name: 'saveDraft',
        title: 'Save Draft',
        onClick: () => {
          onSaveDraft();
        },
        isPrimary: true,
        disabled: disabledPage,
        hidden: isClosed || data?.Status?.toLowerCase() !== 'new',
      },
      {
        name: 'calculateAndSave',
        title: 'Calculate & Save',
        onClick: () => {
          onSubmit();
        },
        isPrimary: true,
        disabled: disabledPage,
        hidden: isClosed || !isValidNumber(data?.LineId?.ExternalCode),
      },
      {
        name: 'issuePolicy',
        title: 'Issue Policy',
        onClick: () => {
          const confirmation: IConfirmation = {
            open: true,
            title: 'Confirmation',
            message: `Are you sure you want to issue the selected policy?`,
            callback: async () => {
              onIssuePolicyAction();
            },
            submitButtonText: 'Yes',
            cancelButtonText: 'No',
          };
          dispatch(OpenConfirmationAction(confirmation));
        },
        isPrimary: true,
        disabled: disabledPage,
        hidden:
          isClosed ||
          !(
            data?.Status?.toLowerCase() === 'in_progress' &&
            isValidNumber(data?.LineId?.ExternalCode)
          ),
      },
      {
        name: 'downloadPolicySample',
        title: 'Download Policy Sample',
        onClick: () => {
          downloadDocument();
        },
        isPrimary: false,
        disabled: disabledPage,
        hidden:
          isClosed ||
          !(
            data?.Status?.toLowerCase() === 'in_progress' &&
            isValidNumber(data?.LineId?.ExternalCode)
          ),
      },
      {
        name: 'closeProposal',
        title: 'Close Proposal',
        onClick: () => {
          setCloseProposalDrawerOpen(true);
        },
        isPrimary: false,
        disabled: disabledPage,
        hidden: isClosed,
      },
    ];

    return <SimpleActionBar items={actions} />;
  };

  const renderMainChildren = () => {
    if (data) {
      // const isClosed = status === "closedwon" || "closedlost";
      const isProposalWon = data.Status?.toUpperCase() === 'CLOSED_WON';
      const isProposalUserNew = data.Status?.toLowerCase() === 'new';
      const steps = isProposalUserNew
        ? ['New', 'In Progress', 'Closed Won']
        : ['New', 'In Progress', isProposalWon ? 'Closed Won' : 'Closed Lost'];

      return (
        <section>
          <div className={stepperClasses.stepperContainer}>
            <div className={stepperClasses.stepperWrapper}>
              <EnhancedStepper
                activeStep={getProposalStatus(steps, data.Status)}
                steps={steps}
                buttonTitle="Calculate & Save"
                buttonOnClick={() => undefined}
              />
            </div>
            <div className={stepperClasses.actionsWrapper}>
              {renderActions()}
            </div>
          </div>
          <div className={tabClasses.tabSplitBody}>
            <div className={tabClasses.tabMainBody}>
              <PolicyDetailsWidget data={data as any} />
              <PolicyCoversRepeaterWidget
                defaultPlanCoversList={planCoversList}
                pageState={pageState as any}
                disabledForm={disabledPage}
                status={data.Status}
                data={data as any}
                allowPlanCoverEdits={data.BusinessUserID.AllowPlanCoverEdits}
                onPageStateUpdate={onPageStateUpdate as any}
                lovs={lovs}
              />
              <PolicyCostChargesWidget
                pageState={pageState as any}
                disabledForm={disabledPage}
                status={data.Status}
                editableLine={data.BusinessUserID?.EditableLineChargesIDs?.includes(
                  data?.LineId.Id
                )}
                line={data?.LineId.ExternalCode?.toString() || ''}
                onPageStateUpdate={onPageStateUpdate as any}
                currencySymbol={data.PolicyCurrency.Symbol}
              />
              <WidgetPaper>
                <WidgetSection title="Payer & Owner">
                  <PolicyPayerWidget
                    pageState={pageState as any}
                    onPageStateUpdate={onPageStateUpdate as any}
                    disabledForm={disabledPage}
                    data={data as any}
                  />
                  {ownerIsDifferent && (
                    <PolicyOwnerWidget
                      pageState={pageState as any}
                      onPageStateUpdate={onPageStateUpdate as any}
                      disabledForm={disabledPage}
                      data={data as any}
                    />
                  )}
                </WidgetSection>
              </WidgetPaper>

              <PolicyInsuredWidget
                pageState={pageState as any}
                onPageStateUpdate={onPageStateUpdate as any}
                disabledForm={disabledPage}
                maxNumber={data.PlanID.MaxNbInsured}
                data={data as any}
              />

              <PolicyBeneficiaryWidget
                pageState={pageState as any}
                onPageStateUpdate={onPageStateUpdate as any}
                lovs={lovs}
                disabledForm={disabledPage}
                maxNumber={data.PlanID.MaxNbBeneficiary}
                data={data as any}
              />

              <ProposalExpatDetailsWidget
                defaultPlanCoversList={planCoversList}
                pageState={pageState as any}
                status={data.Status}
                allowPlanCoverEdits={data.BusinessUserID.AllowPlanCoverEdits}
                onPageStateUpdate={onPageStateUpdate as any}
                lovs={lovs}
                disabledForm={disabledPage}
              />
              {closeProposalDrawerOpen && (
                <CloseProposalDrawer
                  open={closeProposalDrawerOpen}
                  onClose={() => {
                    setCloseProposalDrawerOpen(false);
                  }}
                  onSuccess={() => {
                    initialize();
                    setCloseProposalDrawerOpen(false);
                  }}
                  id={entityId}
                />
              )}
            </div>
            <div className={tabClasses.tabRightSection}>
              <PolicyProposalQuoteWidget
                data={data as any}
                covers={coversList}
              />
              {data?.Status && data?.Status?.toLowerCase() !== 'new' && (
                <ComissionProposalWidget
                  data={data as any}
                  onUpdateSuccess={() => {
                    initialize();
                  }}
                />
              )}
            </div>
          </div>
        </section>
      );
    }

    return <></>;
  };

  return loading ? (
    <Loader />
  ) : (
    <>
      <StaticLayout
        name={'Proposal Details'}
        leftChildren={renderLeftSection()}
        mainChildren={renderMainChildren()}
      />
    </>
  );
};

export default ProposalExpatDetailsPage;

function getFileDetailsList(data: any): FileDetail[] {
  const fileDetails: FileDetail[] = [];

  const items = data.production.entities.proposal.production.issuePolicyExpat;

  const fileDetail: FileDetail = {
    EntityTypeId: items.EntityTypeId,
    EntityId: items.EntityId,
    EntityViewId: items.EntityViewId,
    PropertyId: items.PropertyId,
    fileId: items.File.id,
    fileName: items.File.fileName,
  };
  fileDetails.push(fileDetail);

  return fileDetails;
}

function getFileDetails(data: any) {
  const downloadPolicySampleExpat =
    data.production.entities.proposal.production.downloadPolicySampleExpat;
  const { EntityTypeId, EntityId, EntityViewId, PropertyId, File } =
    downloadPolicySampleExpat;
  const { id: fileId, fileName } = File;

  return {
    EntityTypeId,
    EntityId,
    EntityViewId,
    PropertyId,
    fileId,
    fileName,
  };
}

type FileDetail = {
  EntityTypeId: string;
  EntityId: string;
  EntityViewId: string;
  PropertyId: string;
  fileId: string;
  fileName: string;
};
